import { _ as _ts_decorate } from "@swc/helpers/_/_ts_decorate";
import { _ as _ts_metadata } from "@swc/helpers/_/_ts_metadata";
import { action, computed, flow, makeObservable, observable, runInAction } from "mobx";
import { BaseOptions, locationRestKeyValueMapPatch } from "@devowl-wp/utils";
import { BannerLinkCollection } from "../models/bannerLinkCollection.js";
import { BannerLinkModel } from "../models/bannerLinkModel.js";
import { request } from "../utils/request.js";
import { locationRestCountryBypassDatabasePut } from "../wp-api/countryBypassDatabase.put.js";
import { locationRestMigrationDelete } from "../wp-api/migration.delete.js";
import { locationRestNavMenuAddLinksPost } from "../wp-api/navMenuAddLinks.post.js";
import { locationRestRevisionCurrentGet } from "../wp-api/revisionCurrent.get.js";
import { locationRestRevisionCurrentPut } from "../wp-api/revisionCurrent.put.js";
import { locationRestSettings } from "../wp-api/settings.get.js";
import { locationRestSettingsPatch } from "../wp-api/settings.patch.js";
class OptionStore extends BaseOptions {
    busySettings = false;
    busyCountryBypassUpdate = false;
    busyAddLinksToNavigationMenu = false;
    needsRevisionRetrigger = false;
    fetchedBannerLinks = false;
    bannerLinks;
    publicCookieCount = 0;
    allCookieCount = 0;
    allBlockerCount = 0;
    allTcfVendorConfigurationCount = 0;
    allScannerResultTemplatesCount = 0;
    allScannerResultExternalUrlsCount = 0;
    cookieCounts = {
        draft: 0,
        private: 0,
        publish: 0
    };
    cloudReleaseInfo = {
        blocker: null,
        service: null
    };
    navMenus = [];
    tcfVendorConfigurationCounts;
    templateNeedsUpdate = [];
    checkSavingConsentViaRestApiEndpointWorkingHtml;
    templateUpdateNoticeHtml;
    templateSuccessorsNoticeHtml;
    googleConsentModeNoticesHtml = [];
    servicesDataProcessingInUnsafeCountriesNoticeHtml = "";
    servicesWithEmptyPrivacyPolicyNoticeHtml = "";
    createdTagManagers = {
        gtm: [],
        mtm: []
    };
    contexts = {
        "": ""
    };
    dashboardMigration;
    fomoCoupon;
    isBannerActive = false;
    isBlockerActive = false;
    hidePageIds = [];
    setCookiesViaManager;
    operatorCountry;
    operatorContactAddress;
    operatorContactPhone;
    operatorContactEmail;
    operatorContactFormId;
    cookiePolicyId;
    territorialLegalBasis;
    isAcceptAllForBots;
    isRespectDoNotTrack;
    isBannerLessConsent;
    bannerLessConsentShowOnPageIds;
    cookieDuration;
    failedConsentDocumentationHandling;
    isSaveIp;
    isDataProcessingInUnsafeCountries;
    isAgeNotice;
    ageNoticeAgeLimit;
    isListServicesNotice;
    isConsentForwarding;
    forwardTo = [];
    crossDomains;
    // Affiliate settings are only available and registered in PRO version
    affiliateLink;
    affiliateLabelBehind;
    affiliateLabelDescription;
    // Country Bypass settings (all optional for free version)
    isCountryBypass;
    countryBypassCountries = [];
    countryBypassType;
    countryBypassDbDownloadTime;
    // TCF compatibility settings (all optional for free version)
    isTcf = false;
    tcfAcceptedTime;
    tcfGvlDownloadTime;
    isGcm = false;
    isGcmShowRecommandationsWithoutConsent = false;
    isGcmCollectAdditionalDataViaUrlParameters = false;
    isGcmRedactAdsDataWithoutConsent = true;
    isGcmListPurposes = true;
    consentDuration;
    consentsDeletedAt;
    bannerlessConsentChecks = {
        essential: [],
        legalBasisConsentWithoutVisualContentBlocker: [],
        legalBasisLegitimateInterest: []
    };
    isBannerStickyLinksEnabled = false;
    // Implement "others" property in your Assets.php;
    others;
    /**
     * Ignore real-cookie-banner cookie as it is hidden when no other cookie is created.
     */ get isOnlyRcbCookieCreated() {
        return this.allCookieCount === 1 && (!this.isTcf || !this.allTcfVendorConfigurationCount);
    }
    get areSettingsFetched() {
        return this.isRespectDoNotTrack !== undefined;
    }
    pureSlug;
    pureSlugCamelCased;
    rootStore;
    constructor(rootStore){
        super();
        makeObservable(this);
        this.rootStore = rootStore;
        this.pureSlug = BaseOptions.getPureSlug(process.env.slug);
        this.pureSlugCamelCased = BaseOptions.getPureSlug(process.env.slug, true);
        // Use the localized WP object to fill this object values.
        runInAction(()=>{
            Object.assign(this, window[this.pureSlugCamelCased]);
            this.bannerLinks = new BannerLinkCollection(this);
            this.fomoCoupon = this.others.fomoCoupon;
        });
    }
    fetchSettings = flow(function*(setFrom) {
        this.busySettings = true;
        try {
            const result = setFrom || (yield request({
                location: locationRestSettings
            }));
            this.isBannerActive = result["rcb-banner-active"];
            this.isBlockerActive = result["rcb-blocker-active"];
            this.hidePageIds = (result["rcb-hide-page-ids"] || "").split(",").map(Number).filter(Boolean);
            this.setCookiesViaManager = result["rcb-set-cookies-via-manager"] || "none";
            this.operatorCountry = result["rcb-operator-country"];
            this.operatorContactAddress = result["rcb-operator-contact-address"];
            this.operatorContactPhone = result["rcb-operator-contact-phone"];
            this.operatorContactEmail = result["rcb-operator-contact-email"];
            this.operatorContactFormId = result["rcb-operator-contact-form-id"];
            this.cookiePolicyId = result["rcb-cookie-policy-id"];
            this.territorialLegalBasis = result["rcb-territorial-legal-basis"].split(",");
            this.isAcceptAllForBots = result["rcb-accept-all-for-bots"];
            this.isRespectDoNotTrack = result["rcb-respect-do-not-track"];
            this.isBannerLessConsent = result["rcb-banner-less-consent"];
            this.bannerLessConsentShowOnPageIds = (result["rcb-banner-less-show-on-page-ids"] || "").split(",").map(Number).filter(Boolean);
            this.cookieDuration = result["rcb-cookie-duration"];
            this.failedConsentDocumentationHandling = result["rcb-failed-consent-documentation-handling"];
            this.isSaveIp = result["rcb-save-ip"];
            this.isDataProcessingInUnsafeCountries = result["rcb-data-processing-in-unsafe-countries"];
            this.isAgeNotice = result["rcb-age-notice"];
            this.ageNoticeAgeLimit = result["rcb-age-notice-age-limit"];
            this.isListServicesNotice = result["rcb-list-services-notice"];
            this.isConsentForwarding = result["rcb-consent-forwarding"] || false;
            this.forwardTo = (result["rcb-forward-to"] || "").split("|").filter(Boolean);
            this.crossDomains = result["rcb-cross-domains"] || "";
            this.isCountryBypass = result["rcb-country-bypass"];
            this.countryBypassCountries = (result["rcb-country-bypass-countries"] || "").split(",").filter(Boolean);
            this.countryBypassType = result["rcb-country-bypass-type"];
            this.countryBypassDbDownloadTime = result["rcb-country-bypass-db-download-time"];
            this.isTcf = result["rcb-tcf"];
            this.tcfAcceptedTime = result["rcb-tcf-accepted-time"];
            this.tcfGvlDownloadTime = result["rcb-tcf-gvl-download-time"];
            this.consentDuration = result["rcb-consent-duration"];
            if (process.env.PLUGIN_CTX === "pro") {
                /* onlypro: start */ this.isGcm = result["rcb-gcm"];
                this.isGcmShowRecommandationsWithoutConsent = result["rcb-gcm-recommandations"];
                this.isGcmCollectAdditionalDataViaUrlParameters = result["rcb-gcm-additional-url-parameters"];
                this.isGcmRedactAdsDataWithoutConsent = result["rcb-gcm-redact-without-consent"];
                this.isGcmListPurposes = result["rcb-gcm-list-purposes"];
                this.affiliateLink = result["rcb-affiliate-link"];
                this.affiliateLabelBehind = result["rcb-affiliate-label-behind"];
                this.affiliateLabelDescription = result["rcb-affiliate-description"];
                this.isBannerStickyLinksEnabled = result["rcb-banner-sticky-links-enabled"];
            /* onlypro: end */ }
            yield this.fetchCurrentRevision();
        } catch (e) {
            console.log(e);
            throw e;
        } finally{
            this.busySettings = false;
        }
    });
    fetchBannerLinks = flow(function*() {
        yield this.bannerLinks.get({
            request: {
                status: [
                    "draft",
                    "publish",
                    "private"
                ]
            },
            params: {
                per_page: 100,
                context: "edit"
            }
        });
        this.fetchedBannerLinks = true;
    });
    updateSettings = flow(function*({ isBannerActive, isBlockerActive, hidePageIds, setCookiesViaManager, operatorCountry, operatorContactAddress, operatorContactEmail, operatorContactFormId, operatorContactPhone, cookiePolicyId, territorialLegalBasis, isAcceptAllForBots, isRespectDoNotTrack, cookieDuration, failedConsentDocumentationHandling, isSaveIp, isDataProcessingInUnsafeCountries, isAgeNotice, isBannerLessConsent, bannerLessConsentShowOnPageIds, ageNoticeAgeLimit, isListServicesNotice, isConsentForwarding, forwardTo, crossDomains, affiliateLink, affiliateLabelBehind, affiliateLabelDescription, isCountryBypass, countryBypassCountries, countryBypassType, isTcf, isGcm, isGcmShowRecommandationsWithoutConsent, isGcmCollectAdditionalDataViaUrlParameters, isGcmRedactAdsDataWithoutConsent, isGcmListPurposes, consentDuration, isBannerStickyLinksEnabled }, bannerLinks) {
        this.busySettings = true;
        try {
            const result = yield request({
                location: locationRestSettingsPatch,
                request: {
                    ...isBannerActive === undefined ? {} : {
                        "rcb-banner-active": isBannerActive
                    },
                    ...isBlockerActive === undefined ? {} : {
                        "rcb-blocker-active": isBlockerActive
                    },
                    ...hidePageIds === undefined ? {} : {
                        "rcb-hide-page-ids": hidePageIds.join(",")
                    },
                    ...setCookiesViaManager === undefined ? {} : {
                        "rcb-set-cookies-via-manager": setCookiesViaManager
                    },
                    ...operatorCountry === undefined ? {} : {
                        "rcb-operator-country": operatorCountry
                    },
                    ...operatorContactAddress === undefined ? {} : {
                        "rcb-operator-contact-address": operatorContactAddress
                    },
                    ...operatorContactPhone === undefined ? {} : {
                        "rcb-operator-contact-phone": operatorContactPhone
                    },
                    ...operatorContactEmail === undefined ? {} : {
                        "rcb-operator-contact-email": operatorContactEmail
                    },
                    ...operatorContactFormId === undefined ? {} : {
                        "rcb-operator-contact-form-id": operatorContactFormId
                    },
                    ...cookiePolicyId === undefined ? {} : {
                        "rcb-cookie-policy-id": cookiePolicyId
                    },
                    ...territorialLegalBasis === undefined ? {} : {
                        "rcb-territorial-legal-basis": territorialLegalBasis.join(",")
                    },
                    ...isAcceptAllForBots === undefined ? {} : {
                        "rcb-accept-all-for-bots": isAcceptAllForBots
                    },
                    ...isBannerLessConsent === undefined ? {} : {
                        "rcb-banner-less-consent": isBannerLessConsent
                    },
                    ...bannerLessConsentShowOnPageIds === undefined ? {} : {
                        "rcb-banner-less-show-on-page-ids": bannerLessConsentShowOnPageIds.join(",")
                    },
                    ...isRespectDoNotTrack === undefined ? {} : {
                        "rcb-respect-do-not-track": isRespectDoNotTrack
                    },
                    ...cookieDuration === undefined ? {} : {
                        "rcb-cookie-duration": cookieDuration
                    },
                    ...failedConsentDocumentationHandling === undefined ? {} : {
                        "rcb-failed-consent-documentation-handling": failedConsentDocumentationHandling
                    },
                    ...isSaveIp === undefined ? {} : {
                        "rcb-save-ip": isSaveIp
                    },
                    ...isDataProcessingInUnsafeCountries === undefined ? {} : {
                        "rcb-data-processing-in-unsafe-countries": isDataProcessingInUnsafeCountries
                    },
                    ...isAgeNotice === undefined ? {} : {
                        "rcb-age-notice": isAgeNotice
                    },
                    ...ageNoticeAgeLimit === undefined ? {} : {
                        "rcb-age-notice-age-limit": ageNoticeAgeLimit
                    },
                    ...isListServicesNotice === undefined ? {} : {
                        "rcb-list-services-notice": isListServicesNotice
                    },
                    ...isConsentForwarding === undefined ? {} : {
                        "rcb-consent-forwarding": isConsentForwarding
                    },
                    ...forwardTo === undefined ? {} : {
                        "rcb-forward-to": forwardTo.join("|")
                    },
                    ...crossDomains === undefined ? {} : {
                        "rcb-cross-domains": crossDomains
                    },
                    ...isCountryBypass === undefined ? {} : {
                        "rcb-country-bypass": isCountryBypass
                    },
                    ...countryBypassCountries === undefined ? {} : {
                        "rcb-country-bypass-countries": countryBypassCountries.join(",")
                    },
                    ...countryBypassType === undefined ? {} : {
                        "rcb-country-bypass-type": countryBypassType
                    },
                    ...isTcf === undefined ? {} : {
                        "rcb-tcf": isTcf
                    },
                    ...process.env.PLUGIN_CTX === "pro" ? isGcm === undefined ? {} : {
                        "rcb-gcm": isGcm
                    } : {},
                    ...process.env.PLUGIN_CTX === "pro" ? isGcmShowRecommandationsWithoutConsent === undefined ? {} : {
                        "rcb-gcm-recommandations": isGcmShowRecommandationsWithoutConsent
                    } : {},
                    ...process.env.PLUGIN_CTX === "pro" ? isGcmCollectAdditionalDataViaUrlParameters === undefined ? {} : {
                        "rcb-gcm-additional-url-parameters": isGcmCollectAdditionalDataViaUrlParameters
                    } : {},
                    ...process.env.PLUGIN_CTX === "pro" ? isGcmRedactAdsDataWithoutConsent === undefined ? {} : {
                        "rcb-gcm-redact-without-consent": isGcmRedactAdsDataWithoutConsent
                    } : {},
                    ...process.env.PLUGIN_CTX === "pro" ? isGcmListPurposes === undefined ? {} : {
                        "rcb-gcm-list-purposes": isGcmListPurposes
                    } : {},
                    ...process.env.PLUGIN_CTX === "pro" ? affiliateLink === undefined ? {} : {
                        "rcb-affiliate-link": affiliateLink
                    } : {},
                    ...process.env.PLUGIN_CTX === "pro" ? affiliateLabelBehind === undefined ? {} : {
                        "rcb-affiliate-label-behind": affiliateLabelBehind
                    } : {},
                    ...process.env.PLUGIN_CTX === "pro" ? affiliateLabelDescription === undefined ? {} : {
                        "rcb-affiliate-description": affiliateLabelDescription
                    } : {},
                    ...consentDuration === undefined ? {} : {
                        "rcb-consent-duration": consentDuration
                    },
                    ...process.env.PLUGIN_CTX === "pro" ? isBannerStickyLinksEnabled === undefined ? {} : {
                        "rcb-banner-sticky-links-enabled": isBannerStickyLinksEnabled
                    } : {}
                }
            });
            if (this.fetchedBannerLinks && bannerLinks) {
                const createdBannerLinks = this.bannerLinks.sortedBannerLinks;
                // Delete banner links
                for (const createdBannerLink of createdBannerLinks){
                    if (!bannerLinks.find(({ id })=>createdBannerLink.data.id === id)) {
                        yield createdBannerLink.delete();
                    }
                }
                for(let i = 0; i < bannerLinks.length; i++){
                    const { isExternalUrl, label, pageType, externalUrl, hideCookieBanner, id, pageId, isTargetBlank } = bannerLinks[i];
                    const newMeta = {
                        isExternalUrl,
                        pageType,
                        externalUrl,
                        hideCookieBanner,
                        pageId,
                        isTargetBlank
                    };
                    if (id) {
                        // Update banner links
                        const existing = createdBannerLinks.find(({ data: { id: existingId } })=>id === existingId);
                        if (existing) {
                            const { data: { title: { raw: oldLabel }, menu_order: oldMenuOrder, meta: { isExternalUrl: oldIsExternalUrl, pageType: oldPageType, externalUrl: oldExternalUrl, hideCookieBanner: oldHideCookieBanner, pageId: oldPageId } } } = existing;
                            if (oldLabel !== label || oldMenuOrder !== i || oldIsExternalUrl !== isExternalUrl || oldPageType !== pageType || oldPageId !== pageId || oldExternalUrl !== externalUrl || oldHideCookieBanner !== hideCookieBanner) {
                                existing.setLabel(label);
                                existing.setOrder(i);
                                existing.setMeta(newMeta);
                                yield existing.patch();
                            }
                        }
                    } else {
                        // Add new banner links
                        const draft = new BannerLinkModel(this.bannerLinks, {
                            title: {
                                raw: label
                            },
                            content: {
                                raw: "",
                                protected: false
                            },
                            status: "publish",
                            menu_order: i,
                            meta: newMeta
                        });
                        yield draft.persist();
                    }
                }
            }
            this.fetchSettings(result);
            this.rootStore.checklistStore.fetchChecklist();
        } catch (e) {
            console.log(e);
            throw e;
        } finally{
            this.busySettings = false;
        }
    });
    fetchCurrentRevision = flow(function*() {
        this.busySettings = true;
        try {
            this.setFromCurrentRevision((yield request({
                location: locationRestRevisionCurrentGet
            })));
        } catch (e) {
            console.log(e);
            throw e;
        } finally{
            this.busySettings = false;
        }
    });
    updateCurrentRevision = flow(function*(data) {
        this.busySettings = true;
        try {
            this.setFromCurrentRevision((yield request({
                location: locationRestRevisionCurrentPut,
                request: data
            })));
        } catch (e) {
            console.log(e);
            throw e;
        } finally{
            this.busySettings = false;
        }
    });
    setModalHintSeen = flow(function*(identifier) {
        this.busySettings = true;
        try {
            this.others.modalHints.push(identifier); // Immediate push as this can never fail
            yield request({
                location: locationRestKeyValueMapPatch,
                params: {
                    name: "rcb-notice-states",
                    key: `modal-hint-${identifier}`
                },
                request: {
                    value: true
                }
            });
        } catch (e) {
            console.log(e);
            throw e;
        } finally{
            this.busySettings = false;
        }
    });
    dismissConfigProNotice = flow(function*() {
        try {
            // Immediate set because it can not fail
            this.others.isConfigProNoticeVisible = false;
            yield request({
                location: locationRestKeyValueMapPatch,
                params: {
                    name: "rcb-notice-states",
                    key: "get-pro-main-button"
                },
                request: {
                    value: true
                }
            });
        } catch (e) {
            console.log(e);
            throw e;
        }
    });
    dismissServiceDataProcessingInUnsafeCountriesNotice = flow(function*() {
        try {
            // Immediate set because it can not fail
            this.servicesDataProcessingInUnsafeCountriesNoticeHtml = "";
            yield request({
                location: locationRestKeyValueMapPatch,
                params: {
                    name: "rcb-notice-states",
                    key: "service-data-processing-in-unsafe-countries"
                },
                request: {
                    value: false
                }
            });
        } catch (e) {
            console.log(e);
            throw e;
        }
    });
    dismissBannerlessConsentLegitimateServicesNotice = flow(function*() {
        try {
            // Immediate set because it can not fail
            yield request({
                location: locationRestKeyValueMapPatch,
                params: {
                    name: "rcb-notice-states",
                    key: "dismissed-bannerless-consent-legint-services"
                },
                request: {
                    value: false
                }
            });
            yield this.fetchCurrentRevision();
        } catch (e) {
            console.log(e);
            throw e;
        }
    });
    dismissBannerlessConsentServicesWithoutVisualContentBlockerNotice = flow(function*() {
        try {
            // Immediate set because it can not fail
            yield request({
                location: locationRestKeyValueMapPatch,
                params: {
                    name: "rcb-notice-states",
                    key: "dismissed-bannerless-consent-services-without-visual-content-blocker"
                },
                request: {
                    value: false
                }
            });
            yield this.fetchCurrentRevision();
        } catch (e) {
            console.log(e);
            throw e;
        }
    });
    dismissMigration = flow(function*() {
        try {
            // Immediate set because it can not fail
            const { id: migration } = this.dashboardMigration;
            this.dashboardMigration = undefined;
            yield request({
                location: locationRestMigrationDelete,
                params: {
                    migration
                }
            });
        } catch (e) {
            console.log(e);
            throw e;
        }
    });
    addLinksToNavigationMenu = flow(function*(id) {
        this.busyAddLinksToNavigationMenu = true;
        try {
            const { success } = yield request({
                location: locationRestNavMenuAddLinksPost,
                request: {
                    id
                }
            });
            if (success) {
                this.rootStore.checklistStore.fetchChecklist();
                yield this.fetchCurrentRevision();
            }
            return success;
        } catch (e) {
            console.log(e);
            throw e;
        } finally{
            this.busyAddLinksToNavigationMenu = false;
        }
    });
    updateCountryBypassDatabase = flow(function*() {
        this.busyCountryBypassUpdate = true;
        try {
            const { dbDownloadTime } = yield request({
                location: locationRestCountryBypassDatabasePut
            });
            this.countryBypassDbDownloadTime = dbDownloadTime;
        } catch (e) {
            console.log(e);
            throw e;
        } finally{
            this.busyCountryBypassUpdate = false;
        }
    });
    setFromCurrentRevision({ contexts, created_tag_managers, needs_retrigger, public_cookie_count, all_cookie_count, all_blocker_count, all_tcf_vendor_configuration_count, all_scanner_result_templates_count, all_scanner_result_external_urls_count, cookie_counts, cloud_release_info, consents_deleted_at, bannerless_consent_checks, nav_menus, tcf_vendor_configuration_counts, dashboard_migration, fomo_coupon, template_needs_update, check_saving_consent_via_rest_api_endpoint_working_html, template_update_notice_html, template_successors_notice_html, google_consent_mode_notices_html, services_data_processing_in_unsafe_countries_notice_html, services_with_empty_privacy_policy_notice_html }) {
        this.createdTagManagers = created_tag_managers;
        this.needsRevisionRetrigger = needs_retrigger;
        this.publicCookieCount = public_cookie_count;
        this.allCookieCount = all_cookie_count;
        this.allBlockerCount = all_blocker_count;
        this.allTcfVendorConfigurationCount = all_tcf_vendor_configuration_count;
        this.allScannerResultTemplatesCount = all_scanner_result_templates_count;
        this.allScannerResultExternalUrlsCount = all_scanner_result_external_urls_count;
        this.templateNeedsUpdate = template_needs_update;
        this.templateUpdateNoticeHtml = template_update_notice_html;
        this.checkSavingConsentViaRestApiEndpointWorkingHtml = check_saving_consent_via_rest_api_endpoint_working_html;
        this.templateSuccessorsNoticeHtml = template_successors_notice_html;
        this.googleConsentModeNoticesHtml = google_consent_mode_notices_html;
        this.servicesDataProcessingInUnsafeCountriesNoticeHtml = services_data_processing_in_unsafe_countries_notice_html;
        this.servicesWithEmptyPrivacyPolicyNoticeHtml = services_with_empty_privacy_policy_notice_html;
        this.cookieCounts = cookie_counts;
        this.cloudReleaseInfo = cloud_release_info;
        this.consentsDeletedAt = consents_deleted_at;
        this.bannerlessConsentChecks = bannerless_consent_checks;
        this.navMenus = nav_menus;
        this.tcfVendorConfigurationCounts = tcf_vendor_configuration_counts;
        this.contexts = contexts;
        this.dashboardMigration = dashboard_migration;
        this.fomoCoupon = fomo_coupon;
    }
    setShowLicenseFormImmediate(state, isLicensed) {
        this.others.showLicenseFormImmediate = state;
        this.others.isLicensed = isLicensed;
    }
}
_ts_decorate([
    observable
], OptionStore.prototype, "busySettings", void 0);
_ts_decorate([
    observable
], OptionStore.prototype, "busyCountryBypassUpdate", void 0);
_ts_decorate([
    observable
], OptionStore.prototype, "busyAddLinksToNavigationMenu", void 0);
_ts_decorate([
    observable
], OptionStore.prototype, "needsRevisionRetrigger", void 0);
_ts_decorate([
    observable
], OptionStore.prototype, "fetchedBannerLinks", void 0);
_ts_decorate([
    observable,
    _ts_metadata("design:type", typeof BannerLinkCollection === "undefined" ? Object : BannerLinkCollection)
], OptionStore.prototype, "bannerLinks", void 0);
_ts_decorate([
    observable,
    _ts_metadata("design:type", Object)
], OptionStore.prototype, "publicCookieCount", void 0);
_ts_decorate([
    observable,
    _ts_metadata("design:type", Object)
], OptionStore.prototype, "allCookieCount", void 0);
_ts_decorate([
    observable,
    _ts_metadata("design:type", Object)
], OptionStore.prototype, "allBlockerCount", void 0);
_ts_decorate([
    observable,
    _ts_metadata("design:type", Object)
], OptionStore.prototype, "allTcfVendorConfigurationCount", void 0);
_ts_decorate([
    observable,
    _ts_metadata("design:type", Object)
], OptionStore.prototype, "allScannerResultTemplatesCount", void 0);
_ts_decorate([
    observable,
    _ts_metadata("design:type", Object)
], OptionStore.prototype, "allScannerResultExternalUrlsCount", void 0);
_ts_decorate([
    observable,
    _ts_metadata("design:type", Object)
], OptionStore.prototype, "cookieCounts", void 0);
_ts_decorate([
    observable,
    _ts_metadata("design:type", Object)
], OptionStore.prototype, "cloudReleaseInfo", void 0);
_ts_decorate([
    observable,
    _ts_metadata("design:type", Object)
], OptionStore.prototype, "navMenus", void 0);
_ts_decorate([
    observable,
    _ts_metadata("design:type", Object)
], OptionStore.prototype, "tcfVendorConfigurationCounts", void 0);
_ts_decorate([
    observable,
    _ts_metadata("design:type", Object)
], OptionStore.prototype, "templateNeedsUpdate", void 0);
_ts_decorate([
    observable,
    _ts_metadata("design:type", Object)
], OptionStore.prototype, "checkSavingConsentViaRestApiEndpointWorkingHtml", void 0);
_ts_decorate([
    observable,
    _ts_metadata("design:type", Object)
], OptionStore.prototype, "templateUpdateNoticeHtml", void 0);
_ts_decorate([
    observable,
    _ts_metadata("design:type", Object)
], OptionStore.prototype, "templateSuccessorsNoticeHtml", void 0);
_ts_decorate([
    observable,
    _ts_metadata("design:type", Object)
], OptionStore.prototype, "googleConsentModeNoticesHtml", void 0);
_ts_decorate([
    observable,
    _ts_metadata("design:type", Object)
], OptionStore.prototype, "servicesDataProcessingInUnsafeCountriesNoticeHtml", void 0);
_ts_decorate([
    observable,
    _ts_metadata("design:type", Object)
], OptionStore.prototype, "servicesWithEmptyPrivacyPolicyNoticeHtml", void 0);
_ts_decorate([
    observable,
    _ts_metadata("design:type", Object)
], OptionStore.prototype, "createdTagManagers", void 0);
_ts_decorate([
    observable,
    _ts_metadata("design:type", Object)
], OptionStore.prototype, "contexts", void 0);
_ts_decorate([
    observable,
    _ts_metadata("design:type", Object)
], OptionStore.prototype, "dashboardMigration", void 0);
_ts_decorate([
    observable,
    _ts_metadata("design:type", Object)
], OptionStore.prototype, "fomoCoupon", void 0);
_ts_decorate([
    observable,
    _ts_metadata("design:type", Object)
], OptionStore.prototype, "isBannerActive", void 0);
_ts_decorate([
    observable,
    _ts_metadata("design:type", Object)
], OptionStore.prototype, "isBlockerActive", void 0);
_ts_decorate([
    observable,
    _ts_metadata("design:type", Object)
], OptionStore.prototype, "hidePageIds", void 0);
_ts_decorate([
    observable,
    _ts_metadata("design:type", Object)
], OptionStore.prototype, "setCookiesViaManager", void 0);
_ts_decorate([
    observable,
    _ts_metadata("design:type", Object)
], OptionStore.prototype, "operatorCountry", void 0);
_ts_decorate([
    observable,
    _ts_metadata("design:type", Object)
], OptionStore.prototype, "operatorContactAddress", void 0);
_ts_decorate([
    observable,
    _ts_metadata("design:type", Object)
], OptionStore.prototype, "operatorContactPhone", void 0);
_ts_decorate([
    observable,
    _ts_metadata("design:type", Object)
], OptionStore.prototype, "operatorContactEmail", void 0);
_ts_decorate([
    observable,
    _ts_metadata("design:type", Object)
], OptionStore.prototype, "operatorContactFormId", void 0);
_ts_decorate([
    observable,
    _ts_metadata("design:type", Object)
], OptionStore.prototype, "cookiePolicyId", void 0);
_ts_decorate([
    observable,
    _ts_metadata("design:type", Object)
], OptionStore.prototype, "territorialLegalBasis", void 0);
_ts_decorate([
    observable,
    _ts_metadata("design:type", Object)
], OptionStore.prototype, "isAcceptAllForBots", void 0);
_ts_decorate([
    observable,
    _ts_metadata("design:type", Object)
], OptionStore.prototype, "isRespectDoNotTrack", void 0);
_ts_decorate([
    observable,
    _ts_metadata("design:type", Object)
], OptionStore.prototype, "isBannerLessConsent", void 0);
_ts_decorate([
    observable,
    _ts_metadata("design:type", Object)
], OptionStore.prototype, "bannerLessConsentShowOnPageIds", void 0);
_ts_decorate([
    observable,
    _ts_metadata("design:type", Object)
], OptionStore.prototype, "cookieDuration", void 0);
_ts_decorate([
    observable,
    _ts_metadata("design:type", Object)
], OptionStore.prototype, "failedConsentDocumentationHandling", void 0);
_ts_decorate([
    observable,
    _ts_metadata("design:type", Object)
], OptionStore.prototype, "isSaveIp", void 0);
_ts_decorate([
    observable,
    _ts_metadata("design:type", Object)
], OptionStore.prototype, "isDataProcessingInUnsafeCountries", void 0);
_ts_decorate([
    observable,
    _ts_metadata("design:type", Object)
], OptionStore.prototype, "isAgeNotice", void 0);
_ts_decorate([
    observable,
    _ts_metadata("design:type", Object)
], OptionStore.prototype, "ageNoticeAgeLimit", void 0);
_ts_decorate([
    observable,
    _ts_metadata("design:type", Object)
], OptionStore.prototype, "isListServicesNotice", void 0);
_ts_decorate([
    observable,
    _ts_metadata("design:type", Object)
], OptionStore.prototype, "isConsentForwarding", void 0);
_ts_decorate([
    observable,
    _ts_metadata("design:type", Object)
], OptionStore.prototype, "forwardTo", void 0);
_ts_decorate([
    observable,
    _ts_metadata("design:type", Object)
], OptionStore.prototype, "crossDomains", void 0);
_ts_decorate([
    observable,
    _ts_metadata("design:type", Object)
], OptionStore.prototype, "affiliateLink", void 0);
_ts_decorate([
    observable,
    _ts_metadata("design:type", Object)
], OptionStore.prototype, "affiliateLabelBehind", void 0);
_ts_decorate([
    observable,
    _ts_metadata("design:type", Object)
], OptionStore.prototype, "affiliateLabelDescription", void 0);
_ts_decorate([
    observable,
    _ts_metadata("design:type", Object)
], OptionStore.prototype, "isCountryBypass", void 0);
_ts_decorate([
    observable,
    _ts_metadata("design:type", Object)
], OptionStore.prototype, "countryBypassCountries", void 0);
_ts_decorate([
    observable,
    _ts_metadata("design:type", Object)
], OptionStore.prototype, "countryBypassType", void 0);
_ts_decorate([
    observable,
    _ts_metadata("design:type", Object)
], OptionStore.prototype, "countryBypassDbDownloadTime", void 0);
_ts_decorate([
    observable,
    _ts_metadata("design:type", Object)
], OptionStore.prototype, "isTcf", void 0);
_ts_decorate([
    observable,
    _ts_metadata("design:type", Object)
], OptionStore.prototype, "tcfAcceptedTime", void 0);
_ts_decorate([
    observable,
    _ts_metadata("design:type", Object)
], OptionStore.prototype, "tcfGvlDownloadTime", void 0);
_ts_decorate([
    observable,
    _ts_metadata("design:type", Object)
], OptionStore.prototype, "isGcm", void 0);
_ts_decorate([
    observable,
    _ts_metadata("design:type", Object)
], OptionStore.prototype, "isGcmShowRecommandationsWithoutConsent", void 0);
_ts_decorate([
    observable,
    _ts_metadata("design:type", Object)
], OptionStore.prototype, "isGcmCollectAdditionalDataViaUrlParameters", void 0);
_ts_decorate([
    observable,
    _ts_metadata("design:type", Object)
], OptionStore.prototype, "isGcmRedactAdsDataWithoutConsent", void 0);
_ts_decorate([
    observable,
    _ts_metadata("design:type", Object)
], OptionStore.prototype, "isGcmListPurposes", void 0);
_ts_decorate([
    observable,
    _ts_metadata("design:type", Object)
], OptionStore.prototype, "consentDuration", void 0);
_ts_decorate([
    observable,
    _ts_metadata("design:type", Object)
], OptionStore.prototype, "consentsDeletedAt", void 0);
_ts_decorate([
    observable,
    _ts_metadata("design:type", Object)
], OptionStore.prototype, "bannerlessConsentChecks", void 0);
_ts_decorate([
    observable,
    _ts_metadata("design:type", Object)
], OptionStore.prototype, "isBannerStickyLinksEnabled", void 0);
_ts_decorate([
    observable,
    _ts_metadata("design:type", typeof OtherOptions === "undefined" ? Object : OtherOptions)
], OptionStore.prototype, "others", void 0);
_ts_decorate([
    computed,
    _ts_metadata("design:type", void 0),
    _ts_metadata("design:paramtypes", [])
], OptionStore.prototype, "isOnlyRcbCookieCreated", null);
_ts_decorate([
    computed,
    _ts_metadata("design:type", void 0),
    _ts_metadata("design:paramtypes", [])
], OptionStore.prototype, "areSettingsFetched", null);
_ts_decorate([
    action,
    _ts_metadata("design:type", Function),
    _ts_metadata("design:paramtypes", [
        typeof ResponseRouteRevisionCurrentGet === "undefined" ? Object : ResponseRouteRevisionCurrentGet
    ]),
    _ts_metadata("design:returntype", void 0)
], OptionStore.prototype, "setFromCurrentRevision", null);
_ts_decorate([
    action,
    _ts_metadata("design:type", Function),
    _ts_metadata("design:paramtypes", [
        Boolean,
        Boolean
    ]),
    _ts_metadata("design:returntype", void 0)
], OptionStore.prototype, "setShowLicenseFormImmediate", null);
export { OptionStore };
